/**
 * Constructs a standardized HTTPS URL with `/net_home` path from a host input,
 * handling protocol variations and validating domain structure.
 *
 * @remarks
 * Performs four key operations:
 * 1. **Protocol Normalization** - Removes any existing protocol prefixes (`http://`, `https://`)
 *    and handles malformed protocols like `https:\\` or `http//`
 * 2. **Host Cleaning** - Converts all slashes to forward slashes, trims extra slashes,
 *    and converts to lowercase
 * 3. **Domain Validation** - Ensures hostname matches `([a-z0-9-]+\.)*[a-z0-9-]+\.[a-z]{2,}$` pattern
 * 4. **Path Enforcement** - Always appends `/net_home` path regardless of input
 *
 * @param host - The input host string which can include:
 * - Protocol variations (`http://`, `https://`, `https:\\`, etc)
 * - Mixed casing (`HTTP://EXAMPLE.COM`)
 * - Extra slashes (`https:///example.com`)
 * - Partial paths (`example.com/path` - will be stripped)
 *
 * @returns A properly formatted HTTPS URL with `/net_home` path, or empty string if:
 * - Input is empty
 * - Hostname structure is invalid
 * - Validation throws an error
 *
 * @example
 * // Valid inputs
 * getFullUrl('example.com') → 'https://example.com/net_home'
 * getFullUrl('HTTP://EXAMPLE.COM') → 'https://example.com/net_home'
 * getFullUrl('https:\\\\example.com') → 'https://example.com/net_home'
 *
 * @example
 * // Invalid returns
 * getFullUrl('invalid host') → ''
 * getFullUrl('example.c') → ''
 * getFullUrl('') → ''
 *
 * @see {@link authUrl} for the validation rules this implementation aligns with
 * @see {@link getFullUrl.spec.ts} for detailed test cases
 */

export function getFullUrl(host: string): string {
  try {
    // Normalize protocol variations and clean host
    const cleanHost = host
      .replace(/^https?:?[\\/]*/i, '') // Remove any protocol prefix with mixed slashes
      .replace(/[\\/]+/g, '/') // Normalize all slashes to forward slashes
      .replace(/^\/+|\/+$/g, '') // Trim leading/trailing slashes
      .toLowerCase();

    // Extract hostname only (ignore any path)
    const [hostname] = cleanHost.split('/');

    // Validate hostname structure
    const isValidHost = /^([a-z0-9-]+\.)*[a-z0-9-]+\.[a-z]{2,}$/.test(hostname);
    const isEmpty = hostname === '';

    if (isEmpty || !isValidHost) {
      console.warn(`[WARN] Invalid host structure: >>>${cleanHost}<<<`);
      return '';
    }

    // Always use /net_home path regardless of input
    return `https://${hostname}/net_home`;
  } catch (error) {
    console.error(`[ERROR] Invalid host structure: >>>${host}<<<`, error);
    return '';
  }
}
